import { useMutation, useQuery } from "@tanstack/react-query";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { get, post } from "api";
import { QRScanner } from "organisms/qr-scanner/QRScanner";
import { queryClient } from "pages/Root";

const pickupItem = debounce(
  ({
    scannedCode,
    platformId,
    retailProductVariantId,
    productId,
    quantity,
    platformLocationId,
    isFromMembership,
  }) => {
    return post("/retailproducts/{platformId}/pickup", {
      platformId: platformId,
      retailProductVariantId: retailProductVariantId,
      productId: productId,
      quantity: quantity,
      platformLocationId: platformLocationId,
      isFromMembership: isFromMembership,
      scannedCode: scannedCode,
    });
  },
  500,
  { leading: true, trailing: false }
);

const ScanPickupRetailProductsItem = () => {
  const [scannedQRCode, setScannedQRCode] = useState("");
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const navigate = useNavigate();
  const { state } = useLocation();
  const [errorMessage, setErrorMessage] = useState(
    "Something went wrong - please contact support if this issue persists"
  );

  const { data: platform } = useQuery({
    queryKey: ["/platform"],
    queryFn: () => get("/platform"),
    staleTime: Infinity,
  });

  const mutation = useMutation({
    mutationFn: pickupItem,
    onSuccess: () => {
      setScannedQRCode("");
      queryClient.invalidateQueries(["/retailproducts"]);
      navigate(-1);
    },
    onError: (err: unknown) => {
      if (
        (err as { response?: { status?: number } }).response?.status === 400
      ) {
        setErrorMessage("Unrecognized QR code - please try again");
      }
    },
  });

  const handleShowErrorMessage = useCallback(() => {
    if (mutation.isError) {
      setShowErrorMessage(true);
      return setTimeout(() => {
        setShowErrorMessage(false);
        setScannedQRCode("");
      }, 2000);
    }
  }, [mutation.isError]);

  useEffect(() => {
    const timeoutId = handleShowErrorMessage();
    return () => clearTimeout(timeoutId);
  }, [handleShowErrorMessage]);

  const handleScanResult = useCallback(() => {
    if (
      scannedQRCode &&
      !showErrorMessage &&
      !mutation.isLoading &&
      !mutation.isSuccess
    ) {
      if (!state.retailProductVariantId) {
        setErrorMessage("Product variant information is missing");
        setShowErrorMessage(true);
        return;
      }
      if (!state.productId) {
        setErrorMessage("Product ID is missing");
        setShowErrorMessage(true);
        return;
      }
      if (!state.quantity) {
        setErrorMessage("Quantity is missing");
        setShowErrorMessage(true);
        return;
      }
      if (state.platformLocationId === undefined) {
        setErrorMessage("Location information is missing");
        setShowErrorMessage(true);
        return;
      }
      mutation.mutate({
        scannedCode: scannedQRCode,
        platformId: platform?.id,
        retailProductVariantId: state.retailProductVariantId,
        productId: state.productId,
        quantity: state.quantity,
        platformLocationId: state.platformLocationId,
        isFromMembership: state.isFromMembership,
      });
    }
  }, [
    scannedQRCode,
    showErrorMessage,
    mutation,
    platform?.id,
    state.retailProductVariantId,
    state.productId,
    state.quantity,
    state.platformLocationId,
    state.isFromMembership,
  ]);

  useEffect(() => {
    handleScanResult();
  }, [handleScanResult]);

  return (
    <QRScanner
      errorMessage={showErrorMessage ? errorMessage : undefined}
      onClose={() => navigate(-1)}
      onResult={setScannedQRCode}
    />
  );
};

export default ScanPickupRetailProductsItem;
